import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Button, Progress, List, Avatar } from 'antd';
import { 
  FolderOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  PlusOutlined,
  FileTextOutlined,
  UserOutlined,
  TeamOutlined,
  DatabaseOutlined,
  TrophyOutlined,
  RiseOutlined,
  EyeOutlined,
  EditOutlined,
  SafetyOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchDossierStats, fetchDossiers } from '../../store/slices/dossierSlice';
import { fetchUsers } from '../../store/slices/referenceSlice';
import { RootState, AppDispatch } from '../../store';
import { Dossier, User } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import { 
  DashboardContainer, 
  StatsCard, 
  RecentDossiersCard,
  PageHeader,
  QuickActions,
  ChartCard,
  UserStatsCard,
  ProgressCard,
} from './Dashboard.styles';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { stats, dossiers, loading } = useSelector((state: RootState) => state.dossiers);
  const { users } = useSelector((state: RootState) => state.references);
  const { user } = useSelector((state: RootState) => state.auth);

  const [dossiersByConservation, setDossiersByConservation] = useState<{ [key: string]: number }>({});
  const [userProductivity, setUserProductivity] = useState<{ user: User; count: number }[]>([]);

  useEffect(() => {
    dispatch(fetchDossierStats());
    dispatch(fetchDossiers({ page: 1 }));
    if (user?.is_gestionnaire) {
      dispatch(fetchUsers());
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (dossiers.length > 0) {
      // Group dossiers by conservation
      const conservationGroups = dossiers.reduce((acc, dossier) => {
        acc[dossier.conservation] = (acc[dossier.conservation] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number });
      setDossiersByConservation(conservationGroups);

      // Calculate user productivity
      if (users.length > 0) {
        const productivity = users
          .filter(u => u.role === 'gestionnaire')
          .map(u => ({
            user: u,
            count: dossiers.filter(d => d.fk_user_gestionnaire === u.id).length
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
        setUserProductivity(productivity);
      }
    }
  }, [dossiers, users]);

  const getEtatColor = (etat: string) => {
  const colors = {
    en_attente_visite: "blue",
    en_cours_visite: "processing",
    en_attente_elaboration: "orange",
    en_cours_elaboration: "processing",
    en_attente_validation: "gold",
    en_cours_validation: "processing",
    en_attente_modification: "red",
    en_cours_modification: "processing",
    modifie_att_validation: "purple",  // NOUVEAU
    valide_att_honoraires: "purple",
    valide_a_envoyer: "green",
    envoye: "success",
  };
  return colors[etat as keyof typeof colors] || "default";
};

const getEtatText = (etat: string) => {
  const texts = {
    en_attente_visite: "En attente de visite",
    en_cours_visite: "En cours de visite",
    en_attente_elaboration: "En attente d'élaboration",
    en_cours_elaboration: "En cours d'élaboration",
    en_attente_validation: "En attente de validation",
    en_cours_validation: "En cours de validation",
    en_attente_modification: "En attente de modification",
    en_cours_modification: "En cours de modification",
    modifie_att_validation: "Modifié - Att. validation",  // NOUVEAU
    valide_att_honoraires: "Validé - Att. honoraires",
    valide_a_envoyer: "Validé - À envoyer",
    envoye: "Envoyé",
  };
  return texts[etat as keyof typeof texts] || etat;
};

  const columns = [
    {
      title: 'Numéro',
      dataIndex: 'numero_dossier',
      key: 'numero_dossier',
      render: (text: string, record: Dossier) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/dossiers/${record.id}`)}
          style={{ padding: 0, fontWeight: 500 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      ellipsis: true,
    },
    {
      title: 'Typologie',
      dataIndex: 'typologie_libelle',
      key: 'typologie_libelle',
      ellipsis: true,
    },
    {
      title: 'État',
      dataIndex: 'etat_dossier',
      key: 'etat_dossier',
      render: (etat: string) => (
        <Tag color={getEtatColor(etat)}>
          {getEtatText(etat)}
        </Tag>
      ),
    },
    {
      title: 'Date réception',
      dataIndex: 'Date_reception_demande',
      key: 'Date_reception_demande',
      render: (date: string) => formatDate(date),
    },
  ];

  const recentDossiers = dossiers.slice(0, 5);
  const completionRate = stats ? Math.round((stats.termines / stats.total) * 100) : 0;
  const inProgressRate = stats ? Math.round((stats.en_cours / stats.total) * 100) : 0;

  return (
    <DashboardContainer>
      <PageHeader>
        <h1>Tableau de bord</h1>
        <p>Bienvenue, {user?.first_name} {user?.last_name}</p>
      </PageHeader>

      <QuickActions>
        {user?.is_gestionnaire && (
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => navigate('/dossiers/new')}
            size="large"
          >
            Nouveau dossier
          </Button>
        )}
        <Button 
          icon={<FileTextOutlined />}
          onClick={() => navigate('/dossiers')}
          size="large"
        >
          Voir tous les dossiers
        </Button>
        {user?.is_gestionnaire && (
          <Button 
            icon={<TeamOutlined />}
            onClick={() => navigate('/users')}
            size="large"
          >
            Gestion utilisateurs
          </Button>
        )}
      </QuickActions>

      {/* Main Statistics */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard>
            <Statistic
              title="Total dossiers"
              value={stats?.total || 0}
              prefix={<FolderOutlined style={{ color: '#1890ff' }} />}
            />
          </StatsCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard>
            <Statistic
              title="En cours"
              value={stats?.en_cours || 0}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
            />
          </StatsCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard>
            <Statistic
              title="Terminés"
              value={stats?.termines || 0}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </StatsCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard>
            <Statistic
              title="En attente"
              value={stats?.en_attente || 0}
              prefix={<ExclamationCircleOutlined style={{ color: '#722ed1' }} />}
            />
          </StatsCard>
        </Col>
      </Row>

      {/* Detailed workflow stats */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} sm={8}>
          <StatsCard>
            <Statistic
              title="Phase Visite"
              value={`${(stats?.detail?.visite?.en_attente || 0) + (stats?.detail?.visite?.en_cours || 0)}`}
              prefix={<EyeOutlined style={{ color: '#1890ff' }} />}
              suffix={
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {stats?.detail?.visite?.en_attente || 0} att. / {stats?.detail?.visite?.en_cours || 0} cours
                </div>
              }
            />
          </StatsCard>
        </Col>
        <Col xs={24} sm={8}>
          <StatsCard>
            <Statistic
              title="Phase Élaboration"
              value={`${(stats?.detail?.elaboration?.en_attente || 0) + (stats?.detail?.elaboration?.en_cours || 0)}`}
              prefix={<EditOutlined style={{ color: '#722ed1' }} />}
              suffix={
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {stats?.detail?.elaboration?.en_attente || 0} att. / {stats?.detail?.elaboration?.en_cours || 0} cours
                </div>
              }
            />
          </StatsCard>
        </Col>
        <Col xs={24} sm={8}>
          <StatsCard>
            <Statistic
              title="Phase Validation"
              value={`${(stats?.detail?.validation?.en_attente || 0) + (stats?.detail?.validation?.en_cours || 0)}`}
              prefix={<SafetyOutlined style={{ color: '#52c41a' }} />}
              suffix={
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {stats?.detail?.validation?.en_attente || 0} att. / {stats?.detail?.validation?.en_cours || 0} cours
                </div>
              }
            />
          </StatsCard>
        </Col>
      </Row>

      {/* Progress and Charts */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <ProgressCard title="Taux de completion">
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={completionRate}
                strokeColor="#52c41a"
                format={(percent) => `${percent}%`}
                size={120}
              />
              <p style={{ marginTop: 16, color: '#8c8c8c' }}>
                Dossiers terminés sur le total
              </p>
            </div>
          </ProgressCard>
        </Col>
        
        <Col xs={24} sm={12} lg={8}>
          <ProgressCard title="Dossiers en cours">
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={inProgressRate}
                strokeColor="#faad14"
                format={(percent) => `${percent}%`}
                size={120}
              />
              <p style={{ marginTop: 16, color: '#8c8c8c' }}>
                Dossiers en cours de traitement
              </p>
            </div>
          </ProgressCard>
        </Col>

        <Col xs={24} lg={8}>
          <ChartCard title="Top Conservations">
            <List
              dataSource={Object.entries(dossiersByConservation)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5)}
              renderItem={([conservation, count]) => (
                <List.Item>
                  <List.Item.Meta
                    title={conservation}
                    description={`${count} dossiers`}
                  />
                  <div>
                    <Progress
                      percent={Math.round((count / (stats?.total || 1)) * 100)}
                      showInfo={false}
                      strokeColor="#1890ff"
                      size="small"
                      style={{ width: 100 }}
                    />
                  </div>
                </List.Item>
              )}
            />
          </ChartCard>
        </Col>
      </Row>

      {/* Detailed Information */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <RecentDossiersCard 
            title="Dossiers récents"
            extra={
              <Button type="link" onClick={() => navigate('/dossiers')}>
                Voir tout
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={recentDossiers}
              rowKey="id"
              pagination={false}
              loading={loading}
              size="small"
            />
          </RecentDossiersCard>
        </Col>

        {user?.is_gestionnaire && (
          <Col xs={24} lg={8}>
            <UserStatsCard 
              title="Top Gestionnaires"
              extra={
                <Button type="link" onClick={() => navigate('/users')}>
                  Voir tout
                </Button>
              }
            >
              <List
                dataSource={userProductivity}
                renderItem={(item, index) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          icon={<UserOutlined />} 
                          style={{ 
                            backgroundColor: index === 0 ? '#faad14' : '#1890ff' 
                          }}
                        />
                      }
                      title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {item.user.first_name} {item.user.last_name}
                          {index === 0 && (
                            <TrophyOutlined style={{ color: '#faad14', marginLeft: 8 }} />
                          )}
                        </div>
                      }
                      description={`${item.count} dossiers gérés`}
                    />
                  </List.Item>
                )}
              />
            </UserStatsCard>
          </Col>
        )}
      </Row>

      {/* Quick Stats for Gestionnaires */}
      {user?.is_gestionnaire && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={8}>
            <StatsCard>
              <Statistic
                title="Utilisateurs actifs"
                value={users.filter(u => u.is_active).length}
                prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              />
            </StatsCard>
          </Col>
          <Col xs={24} sm={8}>
            <StatsCard>
              <Statistic
                title="Validés"
                value={stats?.valides || 0}
                prefix={<SafetyOutlined style={{ color: '#722ed1' }} />}
              />
            </StatsCard>
          </Col>
          <Col xs={24} sm={8}>
            <StatsCard>
              <Statistic
                title="En modification"
                value={stats?.en_modification || 0}
                prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              />
            </StatsCard>
          </Col>
        </Row>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;